import { useEffect, useState } from "react";
import { api } from "@/lib/config";
import { toast } from "sonner";
import { X } from "lucide-react";

const STORAGE_KEY = "hopsy_welcome_gift_v1";
const OPEN_DELAY_MS = 4500;

// One-time homepage popup. Mobile: bottom-sheet. Desktop: centred modal.
// Dark-forest card with bright sun/yellow Hopsy wordmark and a sketchy pet trio.
export default function WelcomeGiftModal() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const saved = typeof window !== "undefined" ? localStorage.getItem(STORAGE_KEY) : null;
    if (saved) return;
    const t = setTimeout(() => setOpen(true), OPEN_DELAY_MS);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && close("dismissed");
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open]);

  const close = (reason = "dismissed") => {
    localStorage.setItem(STORAGE_KEY, reason);
    setOpen(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      const { data } = await api.post("/newsletter/welcome-gift", { email: email.trim() });
      setDone(true);
      if (data?.already) {
        toast.info("You're already on the list — your surprise gift is locked in.");
      } else {
        toast.success("Done! Check your inbox for the details.");
      }
      localStorage.setItem(STORAGE_KEY, "claimed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save your email. Try again?");
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div
      data-testid="welcome-gift-modal"
      className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-black/55 backdrop-blur-[3px] animate-[wgFade_.25s_ease-out]"
      onClick={() => close("dismissed")}
    >
      <div
        className="relative w-full sm:max-w-[440px] sm:mx-4 sm:rounded-[22px] rounded-t-3xl overflow-hidden shadow-[0_30px_60px_-15px_rgba(0,0,0,0.45)] animate-[wgSlide_.38s_cubic-bezier(.2,.9,.3,1.08)]"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="welcome-gift-title"
        style={{ backgroundColor: "#2f3e2d" }}
      >
        {/* Decorative corner glow */}
        <div
          aria-hidden
          className="pointer-events-none absolute -top-24 -right-24 w-64 h-64 rounded-full opacity-25 blur-3xl"
          style={{ background: "radial-gradient(circle, #f2c94c 0%, transparent 70%)" }}
        />

        <button
          type="button"
          onClick={() => close("dismissed")}
          aria-label="Close welcome gift"
          data-testid="welcome-gift-close"
          className="absolute top-3 right-3 z-20 w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 text-cream flex items-center justify-center transition border border-white/15 backdrop-blur"
        >
          <X className="w-4 h-4" strokeWidth={1.75} />
        </button>

        {/* Pets illustration */}
        <div className="relative pt-8 px-6 pb-0">
          <img
            src="/welcome-pets-sketch.webp"
            alt="Illustration of a rabbit, guinea pig, and hamster"
            className="block mx-auto h-28 sm:h-32 w-auto object-contain drop-shadow-[0_6px_12px_rgba(0,0,0,0.25)]"
            loading="eager"
          />
        </div>

        {done ? (
          <div className="relative z-10 px-7 sm:px-9 pt-5 pb-8 text-center" data-testid="welcome-gift-done">
            <HopsyLogo />
            <p className="text-[10px] uppercase tracking-[0.3em] mt-5" style={{ color: "#d7b74a" }}>
              Gift · Secured
            </p>
            <h2 className="font-serif text-2xl sm:text-[26px] text-cream mt-3 leading-[1.2]">
              Your surprise is on its way.
            </h2>
            <p className="text-[13px] sm:text-sm text-cream/70 font-light mt-3 leading-relaxed">
              Place your first order with <strong className="text-cream break-all">{email.toLowerCase()}</strong> and
              we'll tuck a surprise gift into the box.
            </p>
            <button
              type="button"
              onClick={() => close("claimed")}
              data-testid="welcome-gift-shop"
              className="mt-6 inline-block px-7 py-3 text-[11px] uppercase tracking-[0.28em] font-semibold rounded-full transition hover:brightness-95"
              style={{ backgroundColor: "#f2c94c", color: "#2f3e2d" }}
            >
              Start shopping
            </button>
          </div>
        ) : (
          <div className="relative z-10 px-7 sm:px-9 pt-5 pb-8 text-center">
            <HopsyLogo />
            <h2
              id="welcome-gift-title"
              className="font-serif text-[24px] sm:text-[28px] text-cream mt-5 leading-[1.18]"
            >
              Sign up today — we'll tuck a <span style={{ color: "#f2c94c" }}>surprise gift</span> into your first order.
            </h2>
            <p className="text-[12.5px] sm:text-[13.5px] text-cream/70 font-light mt-3 leading-relaxed">
              Drop your email and place your first order with Hopsy. That's it — the little one comes for free, chosen with love.
            </p>

            <form onSubmit={submit} className="mt-6 space-y-3">
              <input
                type="email"
                required
                placeholder="you@example.com"
                aria-label="Email address"
                data-testid="welcome-gift-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3.5 text-sm text-cream placeholder-cream/50 rounded-full border border-white/20 bg-white/10 focus:outline-none focus:border-[#f2c94c] focus:bg-white/15 transition"
              />
              <button
                type="submit"
                disabled={loading}
                data-testid="welcome-gift-submit"
                className="w-full py-3.5 text-[11px] uppercase tracking-[0.3em] font-bold rounded-full disabled:opacity-60 transition hover:brightness-95 shadow-[0_6px_16px_-6px_rgba(242,201,76,0.5)]"
                style={{ backgroundColor: "#f2c94c", color: "#2f3e2d" }}
              >
                {loading ? "Saving…" : "Claim my gift"}
              </button>
            </form>

            <p className="text-[10.5px] text-cream/50 font-light mt-4 leading-relaxed">
              Just email — no spam, no pushy discounts. Unsubscribe any time.
            </p>
          </div>
        )}
      </div>

      <style>{`
        @keyframes wgFade { from { opacity: 0; } to { opacity: 1; } }
        @keyframes wgSlide { from { transform: translateY(28px) scale(.98); opacity: .2; } to { transform: translateY(0) scale(1); opacity: 1; } }
      `}</style>
    </div>
  );
}

function HopsyLogo() {
  return (
    <img
      src="/hopsy-logo-yellow.webp"
      alt="Hopsy"
      data-testid="welcome-gift-wordmark"
      className="inline-block h-14 sm:h-16 w-auto drop-shadow-[0_2px_14px_rgba(242,201,76,0.25)]"
    />
  );
}
